/**
 * /ai/codex 展示层常量。结构与 /ai/chatgpt 一致，复用共享组件 McpLanding。
 * 注意事项：
 * 1. 所有可见文案走 i18next.t('codex-xxx', '默认英文')，兼容 SSR 与翻译上报（词库见 public/lang/local/report-codex.json）。
 * 2. 操作步骤的 step.code（终端命令 / config.toml 配置）是代码，不属于翻译文案，保持原样不 i18n。
 * 3. 操作步骤末两步截图走 CDN `${cdnHost}/resources/egg/ai/codex/{name}`；能力区 ability 图三平台共用 `.../ai/chatgpt/{name}`。
 * 4. 翻译 key 规则参照 odm：`{pageId}-{区块}-{描述}`；i18next.t 调用落在本文件，归属 router 的 /ai/codex(pageId=codex)。
 */
var DOC_HREF = 'https://developers.cjdropshipping.com/en/api/api2/mcp.html';

function getConstants(cdnHost) {
  var i18next = require('../mcpLanding/i18nText').getI18next();
  // CDN 图片路径助手：${cdnHost}/resources/egg/ai/codex/{name}（操作步骤截图）
  var img = function (name) {
    return (cdnHost || '') + '/resources/egg/ai/codex/' + name;
  };
  // 能力区 ability1-6 三平台共用 chatgpt 目录下的图
  var abilityImg = function (name) {
    return (cdnHost || '') + '/resources/egg/ai/chatgpt/' + name;
  };

  var HERO = {
    layout: 'split',
    illustration: 'codexPerson',
    badge: i18next.t('codex-hero-badge', 'New MCP Server Integration'),
    title: i18next.t('codex-hero-title', 'Manage Your Dropshipping Business, '),
    titleHighlight: i18next.t('codex-hero-title-highlight', 'Right from Your Codex Workflow'),
    subtitle: i18next.t(
      'codex-hero-subtitle',
      'Connect CJdropshipping to Codex. Search products, sync orders, and track logistics through simple natural language.'
    ),
    primaryCta: i18next.t('codex-hero-cta-primary', 'Start Using Now'),
    secondaryCta: i18next.t('codex-hero-cta-secondary', 'View Documentation'),
    secondaryCtaHref: DOC_HREF,
  };

  var PROMPTS = {
    title: i18next.t('codex-prompts-title', 'Recommended Prompts'),
    subtitle: i18next.t(
      'codex-prompts-subtitle',
      'Click any prompt to copy it, then paste it into Codex to call the CJ MCP server.'
    ),
    copyLabel: i18next.t('codex-prompts-copy', 'Copy'),
    copiedLabel: i18next.t('codex-prompts-copied', 'Copied!'),
    items: [
      { icon: 'search', text: i18next.t('codex-prompt-1', 'Search for "phone case" and show the first 20 results') },
      { icon: 'tag', text: i18next.t('codex-prompt-2', 'What colors and sizes are available for product CJYD2774438?') },
      { icon: 'globe', text: i18next.t('codex-prompt-3', 'What warehouses does CJ have globally? Please list all locations') },
      { icon: 'truck', text: i18next.t('codex-prompt-4', 'Calculate the shipping cost for sending 2 units of variant CJYD277443801AZ from China to the US') },
      { icon: 'order', text: i18next.t('codex-prompt-5', 'Open my shopping cart page so I can review the items in it') },
      { icon: 'card', text: i18next.t('codex-prompt-6', 'Which of my orders are still waiting for payment?') },
      { icon: 'wallet', text: i18next.t('codex-prompt-7', 'How much balance do I currently have in my CJ account?') },
      { icon: 'store', text: i18next.t('codex-prompt-8', 'Show me the product connection mappings between CJ and my Shopify store') },
      { icon: 'gauge', text: i18next.t('codex-prompt-9', 'Check my current API call rate limit status') },
      { icon: 'alert', text: i18next.t('codex-prompt-10', 'Show me all the stores connected to my CJ account') },
    ],
  };

  var CAPABILITIES = {
    title: i18next.t('codex-capabilities-title', 'Intelligent Commerce '),
    titleHighlight: i18next.t('codex-capabilities-title-highlight', 'At Your Fingertips'),
    subtitle: i18next.t(
      'codex-capabilities-subtitle',
      'Ditch the complex dashboards. Speak to your data and let the AI handle the heavy lifting of running your store.'
    ),
    items: [
      {
        image: abilityImg('ability1.jpg'),
        title: i18next.t('codex-capability-1-title', 'Product Search'),
        description: i18next.t(
          'codex-capability-1-desc',
          "Instantly query millions of products from CJdropshipping's global catalog with natural language prompts."
        ),
      },
      {
        image: abilityImg('ability2.jpg'),
        title: i18next.t('codex-capability-2-title', 'Order Management'),
        description: i18next.t(
          'codex-capability-2-desc',
          'Check order status, update details, and sync transactions between your store and CJ without leaving the chat.'
        ),
      },
      {
        image: abilityImg('ability3.jpg'),
        title: i18next.t('codex-capability-3-title', 'Logistics Tracking'),
        description: i18next.t(
          'codex-capability-3-desc',
          'Real-time shipping updates and route predictions. Just ask "Where is order #1234?" and get an instant map link.'
        ),
      },
      {
        image: abilityImg('ability4.jpg'),
        title: i18next.t('codex-capability-4-title', 'Inventory Insights'),
        description: i18next.t(
          'codex-capability-4-desc',
          'Monitor stock levels across global warehouses and receive low-inventory alerts automatically through AI analysis.'
        ),
      },
      {
        image: abilityImg('ability5.jpg'),
        title: i18next.t('codex-capability-5-title', 'AI Commerce Q&A'),
        description: i18next.t(
          'codex-capability-5-desc',
          'Ask business strategy questions like "What are the trending niches for Q4?" and get data-backed answers.'
        ),
      },
      {
        image: abilityImg('ability6.jpg'),
        title: i18next.t('codex-capability-6-title', 'Smart Recommendations'),
        description: i18next.t(
          'codex-capability-6-desc',
          "Personalized product suggestions based on your store's sales history and global market trends."
        ),
      },
    ],
  };

  /* 操作步骤来源：CJMCP 操作文档.docx「Codex APP 流程」（Local Mode，改 config.toml）。
     step.code（命令 / 配置）是代码不翻译；title/label/desc/alt 走 i18next。 */
  var OPERATION = {
    title: i18next.t('codex-operation-title', 'Deployment Process'),
    subtitle: i18next.t(
      'codex-operation-subtitle',
      'Wire the CJ MCP server into Codex with a few local configuration steps and unlock AI-driven supply chain automation.'
    ),
    steps: [
      {
        title: i18next.t('codex-operation-step-1-title', 'Install Prerequisites'),
        label: i18next.t('codex-operation-step-1-label', 'git + Node.js 20+'),
        code: '# Codex local mode needs git and Node.js 20 or above\nnode -v   # should print v20.x or higher\ngit --version',
        desc: i18next.t(
          'codex-operation-step-1-desc',
          'Make sure <strong>git</strong> and <strong>Node.js 20+</strong> are installed on your machine before configuring the MCP server.'
        ),
      },
      {
        title: i18next.t('codex-operation-step-2-title', 'Clone the MCP Repo'),
        label: i18next.t('codex-operation-step-2-label', 'git clone api-mcp'),
        code: 'git clone https://github.com/CJ-dropshipping/api-mcp.git',
        desc: i18next.t('codex-operation-step-2-desc', 'Clone the official CJ MCP server repository to your local machine.'),
      },
      {
        title: i18next.t('codex-operation-step-3-title', 'Locate config.toml'),
        label: i18next.t('codex-operation-step-3-label', 'Find the Codex config file'),
        code: '# Codex config file path\n# macOS:\n~/.codex/config.toml\n# Windows:\n%APPDATA%\\.codex\\config.toml\n\n# Find your node path (needed below):\nwhich node    # macOS\nwhere node    # Windows',
        desc: i18next.t(
          'codex-operation-step-3-desc',
          'Find the Codex <code>config.toml</code> for your OS, and note your absolute <code>node</code> path.'
        ),
      },
      {
        title: i18next.t('codex-operation-step-4-title', 'Configure the MCP Server'),
        label: i18next.t('codex-operation-step-4-label', 'Add CJMCP to config.toml'),
        code: '[mcp_servers.CJMCP]\ncommand = "/PATH/TO/node"\nargs = ["/PATH/TO/api-mcp/dist/mcp-server/index.cjs"]\nstartup_timeout_sec = 120\n\n[mcp_servers.CJMCP.env]\nCJ_ENV = "production"\nCJ_LOG_LEVEL = "info"\nCJ_LOG_FILE = "true"',
        desc: i18next.t(
          'codex-operation-step-4-desc',
          'Add the <strong>CJMCP</strong> server block to <code>config.toml</code>, replacing <code>/PATH/TO/...</code> with your real node and repo paths.'
        ),
      },
      {
        title: i18next.t('codex-operation-step-5-title', 'Restart & Run /mcp'),
        label: i18next.t('codex-operation-step-5-label', 'Show MCP server status'),
        image: img('codex1.jpg'),
        desc: i18next.t(
          'codex-operation-step-5-desc',
          'Restart Codex, then type <code>/mcp</code> (Show MCP server status) in the prompt box to list your configured MCP servers.'
        ),
        alt: i18next.t('codex-operation-step-5-alt', 'Codex prompt box with the /mcp command "Show MCP server status" being entered.'),
      },
      {
        title: i18next.t('codex-operation-step-6-title', 'CJMCP is Enabled'),
        label: i18next.t('codex-operation-step-6-label', 'Start calling CJ MCP tools'),
        image: img('codex2.jpg'),
        desc: i18next.t(
          'codex-operation-step-6-desc',
          'In the MCP panel, <strong>CJMCP</strong> shows as <strong>Enabled</strong> — you can now call the CJ MCP tools right from your prompts.'
        ),
        alt: i18next.t('codex-operation-step-6-alt', 'Codex MCP panel listing CJMCP as Enabled among the configured MCP servers.'),
      },
    ],
  };

  var FAQ = {
    title: i18next.t('codex-faq-title', 'Frequently Asked Questions'),
    subtitle: i18next.t('codex-faq-subtitle', 'Find answers to common questions about our MCP server integration.'),
    items: [
      {
        question: i18next.t('codex-faq-1-question', 'Is the CJ MCP server free to use?'),
        answer: i18next.t(
          'codex-faq-1-answer',
          'Yes, the basic MCP server connection is free for all registered CJdropshipping users. Premium features like advanced analytics and higher API rate limits may require a professional tier subscription.'
        ),
      },
      {
        question: i18next.t('codex-faq-2-question', 'Which AI models are supported?'),
        answer: i18next.t(
          'codex-faq-2-answer',
          'We currently officially support ChatGPT, Claude, and any environment compatible with the Model Context Protocol (MCP) standard.'
        ),
      },
      {
        question: i18next.t('codex-faq-3-question', 'How secure is my business data?'),
        answer: i18next.t(
          'codex-faq-3-answer',
          "Security is our top priority. The MCP server uses encrypted tokens for authentication, and we never store your customer data or store credentials on the AI's servers—only the data required for the specific query is processed."
        ),
      },
    ],
  };

  var CTA = {
    title: i18next.t('codex-cta-title', 'Ready to Automate Your Business?'),
    subtitle: i18next.t(
      'codex-cta-subtitle',
      'Join thousands of smart dropshippers who are already scaling their stores with AI-driven operations.'
    ),
    docButton: i18next.t('codex-cta-doc-button', 'View Documentation'),
    docHref: DOC_HREF,
  };

  return { HERO: HERO, PROMPTS: PROMPTS, CAPABILITIES: CAPABILITIES, OPERATION: OPERATION, FAQ: FAQ, CTA: CTA };
}

module.exports = { getConstants: getConstants };
